'use client';

import { useCommendaOSEntitySchema } from '@commenda/api/commenda-os-dsl';
import {
  Badge,
  Input,
  Label,
  LoadingIndicator,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@commenda/kakashi';
import { useEffect, useMemo } from 'react';
import { COMMENDA_OS_DATA_SOURCE } from '../../constants';
import type { CommendaOSFieldConfig, DataSourceConfig, FormDSL } from '@commenda/types';
import { ContextBindingEditor } from './ContextBindingEditor';
import {
  getContextSourceFields,
  getEntityContextKeys,
} from './context-helpers';

export function LocationEditor({
  props,
  dataSource,
  onUpdateProps,
  onDataSourceChange,
  currentFieldId,
  dsl,
}: {
  props: Record<string, unknown>;
  dataSource?: DataSourceConfig;
  onUpdateProps: (key: string, value: unknown) => void;
  onDataSourceChange: (dataSource: DataSourceConfig | undefined) => void;
  currentFieldId?: string;
  dsl?: FormDSL;
}) {
  const { entitySchema, isLoading } = useCommendaOSEntitySchema('location');

  const allLocationTypeOptions = useMemo(() => {
    if (!entitySchema?.filters?.locationType?.enumOptions) return [];
    return entitySchema.filters.locationType.enumOptions.map((opt) => ({
      value: opt.value,
      label: opt.label,
    }));
  }, [entitySchema]);

  const selectedLocationType = (props?.locationType as string) || '';
  const mode = (props?.mode as 'read' | 'read-write') || 'read-write';
  const defaultCountry = (props?.defaultCountry as string) || 'US';
  const locationEntity =
    (props?.entity as 'corporation' | 'keyPerson') || 'corporation';
  const commendaOS = dataSource?.commendaOS;

  // For keyPerson, only CONTACT_ADDRESS is supported
  const locationTypeOptions = useMemo(() => {
    if (locationEntity === 'keyPerson') {
      return allLocationTypeOptions.filter(
        (opt) => opt.value === 'CONTACT_ADDRESS'
      );
    }
    return allLocationTypeOptions;
  }, [locationEntity, allLocationTypeOptions]);

  const contextEntity =
    locationEntity === 'keyPerson' ? 'keyPerson' : 'corporation';

  const contextSourceFields = useMemo(() => {
    if (!dsl) return [];
    return getContextSourceFields(dsl, currentFieldId, contextEntity);
  }, [dsl, currentFieldId, contextEntity]);

  const formContextKeys = useMemo(() => {
    const requirements = (
      dsl as unknown as {
        form?: { contextRequirements?: { required?: string[] } };
      }
    )?.form?.contextRequirements;
    return requirements?.required || [];
  }, [dsl]);

  useEffect(() => {
    if (!dataSource || dataSource.type !== COMMENDA_OS_DATA_SOURCE) {
      onDataSourceChange({
        type: COMMENDA_OS_DATA_SOURCE,
        commendaOS: {
          entity: 'location',
          contextBindings: [],
        },
      });
    }
  }, [dataSource, onDataSourceChange]);

  const updateCommendaOS = (updates: Partial<CommendaOSFieldConfig>) => {
    onDataSourceChange({
      type: COMMENDA_OS_DATA_SOURCE,
      commendaOS: {
        entity: 'location',
        contextBindings: commendaOS?.contextBindings,
        ...updates,
      },
    });
  };

  const selectedTypeMeta = locationTypeOptions.find(
    (opt) => opt.value === selectedLocationType
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h3 className="txt-small-medium text-text-secondary">
          Location Configuration
        </h3>
        <Badge variant="gray">Commenda OS</Badge>
      </div>

      <div className="space-y-4 pl-4 border-l-2 border-border-secondary">
        {/* Entity Selection */}
        <div className="space-y-2">
          <Label>Entity</Label>
          <Select
            value={locationEntity}
            onValueChange={(value) => {
              onUpdateProps('entity', value);
              onUpdateProps('locationType', '');
              updateCommendaOS({ contextBindings: [] });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="corporation">Corporation</SelectItem>
              <SelectItem value="keyPerson">Key Person</SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            Corporation: location for the business entity. Key Person: personal
            address of a key person.
          </p>
        </div>

        {/* Location Type Selection */}
        <div className="space-y-2">
          <Label>Location Type</Label>
          {isLoading ? (
            <div className="flex items-center gap-2 p-2">
              <LoadingIndicator size={16} />
              <span className="txt-small-regular text-text-tertiary">
                Loading location types...
              </span>
            </div>
          ) : (
            <Select
              value={selectedLocationType}
              onValueChange={(value) => onUpdateProps('locationType', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select location type..." />
              </SelectTrigger>
              <SelectContent>
                {locationTypeOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {selectedTypeMeta && (
            <p className="txt-xs-regular text-text-tertiary">
              Selected: {selectedTypeMeta.label}
            </p>
          )}
        </div>

        {/* Mode Selection */}
        <div className="space-y-2">
          <Label>Mode</Label>
          <Select
            value={mode}
            onValueChange={(value) => onUpdateProps('mode', value)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="read">Read Only</SelectItem>
              <SelectItem value="read-write">Read & Write</SelectItem>
            </SelectContent>
          </Select>
          <p className="txt-xs-regular text-text-tertiary">
            Read Only: Display address but don&apos;t allow editing. Read &
            Write: Display and save changes.
          </p>
        </div>

        {/* Default Country */}
        <div className="space-y-2">
          <Label>Default Country</Label>
          <Input
            value={defaultCountry}
            onChange={(e) => onUpdateProps('defaultCountry', e.target.value)}
            placeholder="US"
          />
          <p className="txt-xs-regular text-text-tertiary">
            ISO country code for default country selection (e.g., US, CA, GB)
          </p>
        </div>

        {/* Optional Fields */}
        <div className="space-y-3">
          <Label>Optional Fields</Label>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showName"
                checked={!!props?.showName}
                onChange={(e) => onUpdateProps('showName', e.target.checked)}
                className="h-4 w-4 rounded border-border-primary text-primary focus:ring-primary"
              />
              <label
                htmlFor="showName"
                className="txt-small-regular text-text-secondary"
              >
                Show Location Name
              </label>
            </div>
            <p className="txt-xs-regular text-text-tertiary pl-6">
              Allow users to enter an optional name for this location (e.g.,
              &quot;Headquarters&quot;)
            </p>
          </div>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="showStartDate"
                checked={!!props?.showStartDate}
                onChange={(e) =>
                  onUpdateProps('showStartDate', e.target.checked)
                }
                className="h-4 w-4 rounded border-border-primary text-primary focus:ring-primary"
              />
              <label
                htmlFor="showStartDate"
                className="txt-small-regular text-text-secondary"
              >
                Show Start Date
              </label>
            </div>
            <p className="txt-xs-regular text-text-tertiary pl-6">
              Allow users to enter an optional start date for when this location
              became active
            </p>
          </div>
        </div>

        {/* Context Source - corporationId or directorId binding depending on entity */}
        <ContextBindingEditor
          entity="corporation"
          contextKeys={getEntityContextKeys('corporation')}
          contextBindings={commendaOS?.contextBindings}
          availableFields={contextSourceFields}
          formContextKeys={formContextKeys}
          onChange={(bindings) =>
            updateCommendaOS({ contextBindings: bindings })
          }
        />
      </div>
    </div>
  );
}
